import { translateParts } from 'aos-helpers/src/helpers/translations/Translations'
import { recurrenceRecurrenceMonths } from 'aos-services/src/services/tasks/types/payload/RecurrencePayloadEnums'
import {
    defaultCustomOnTaskRecurrenceYearly,
    defaultOnTaskRecurrenceYearly,
    TaskRecurrenceYearlyPayload,
} from 'aos-services/src/services/tasks/types/payload/TaskRecurrenceYearlyFormPayload'
import { TaskRecurrenceType } from 'aos-services/src/services/tasks/types/task/TaskRecurrenceType'
import { HStack, MultiSelect, Radio, RadioGroup, RadioIndicator } from 'aos-ui-universal'
import React, { FC } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { NumberField } from './NumberField'
import { NumberRowField } from './NumberRowField'
import { RecurrenceCustomOnComponent } from './RecurrenceCustomOnComponent'
import { TaskRecurrenceFormProps } from './TaskRecurrenceFormProps'

const REGULAR_ON = 'regular'
const CUSTOM_ON = 'custom'

export const TaskRecurrenceYearly: FC<TaskRecurrenceYearlyProps> = ({
    value,
    onChange,
    errors,
    canEdit,
}) => {
    const { t: translate } = useTranslation()

    if (value?.type !== TaskRecurrenceType.Yearly) {
        return null
    }

    const isCustomOn = value.customOn !== undefined && value.customOn !== null

    const changeOnMode = (mode: string) => {
        if (canEdit) {
            onChange(
                mode === REGULAR_ON
                    ? defaultOnTaskRecurrenceYearly
                    : defaultCustomOnTaskRecurrenceYearly,
            )
        }
    }

    const monthsSelect = (
        <NumberRowField
            prefixLabel={translate('of', { ns: 'tasks' })}
            error={errors?.month ? translate('yearly.months.error', { ns: 'tasks' }) : undefined}
        >
            <MultiSelect
                options={recurrenceRecurrenceMonths.map(month => ({
                    value: String(month),
                    label: translateParts(translate)('tasks.recurrence-month-value.' + month),
                }))}
                value={value.month.map(String)}
                onChange={month => onChange({ month: month.map(Number) })}
                summaryLabel={translate('selected', { ns: 'tasks' })}
                allLabel={`${translate('selected', { ns: 'tasks' })}: ${
                    recurrenceRecurrenceMonths.length
                }`}
                size='md'
                disabled={!canEdit}
            />
        </NumberRowField>
    )

    const regularOnRow = (
        <HStack className='shrink-0 items-center gap-4'>
            <Radio value={REGULAR_ON}>
                <RadioIndicator />
            </Radio>
            <NumberRowField
                prefixLabel={translate('on', { ns: 'tasks' })}
                suffixLabel={translate('day', { ns: 'tasks' })}
                error={errors?.on ? translate('monthly.on.error', { ns: 'tasks' }) : undefined}
            >
                <NumberField
                    className='w-16'
                    value={value.on}
                    onChange={on => onChange({ on })}
                    disabled={!canEdit || isCustomOn}
                />
            </NumberRowField>
        </HStack>
    )

    const customOnRow = (
        <HStack className='shrink-0 items-start gap-4'>
            <Radio value={CUSTOM_ON} className='mt-2'>
                <RadioIndicator />
            </Radio>
            <RecurrenceCustomOnComponent
                value={value}
                onChange={onChange}
                errors={errors}
                canEdit={canEdit}
            />
        </HStack>
    )

    return (
        <>
            <NumberRowField
                prefixLabel={translate('every', { ns: 'tasks' })}
                suffixLabel={translate('year', { ns: 'tasks' })}
                error={errors?.every ? translate('every.error', { ns: 'tasks' }) : undefined}
            >
                <NumberField
                    className='w-16'
                    value={value.every}
                    onChange={every => onChange({ every })}
                    disabled={!canEdit}
                />
            </NumberRowField>
            <RadioGroup
                value={isCustomOn ? CUSTOM_ON : REGULAR_ON}
                onChange={changeOnMode}
                isDisabled={!canEdit}
            >
                {regularOnRow}
                {!isCustomOn ? <HStack className='shrink-0 pl-9'>{monthsSelect}</HStack> : null}
                {customOnRow}
                {isCustomOn ? <HStack className='shrink-0 pl-9'>{monthsSelect}</HStack> : null}
            </RadioGroup>
        </>
    )
}

export interface TaskRecurrenceYearlyProps extends TaskRecurrenceFormProps {
    errors?: FieldErrors<TaskRecurrenceYearlyPayload>
}
